import React, { useContext } from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useLocation } from "react-router-dom";
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import PageNotFound from '../../../components/PageNotFound';
import RespondentImportList from './RespondentImportList';
import { parse, filterPaginationWithSize, emptyConnection } from '../../../utils/UrlUtil';

export const ALL_RESPONDENT_IMPORTS = gql`
  query allRespondentImports(
    $accountId: ID!
    $first: Int
    $last: Int
    $before: String
    $after: String
    $sort: RespondentImportSortSpecifier
    $filter: RespondentImportFilter
  ) {
    account(id: $accountId) {
      id
      respondentImports(first: $first, last: $last, before: $before, after: $after, sort: $sort, filter: $filter) {
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        totalCount
        indexFrom
        indexTo
        edges {
          node {
            id
            fileName
            status
            createdOn
            updatedOn
          }
        }
      }
    }
  }
`;

/**
{
  "input": {
     "accountId": "123",
     "importId": "123",
     "overwrite": false
   }
 }
*/
const ACTIVATE_RESPONDENT_IMPORT = gql`
  mutation activateAccountRespondentImport($input: ActivateAccountRespondentImportInput!) {
    activateAccountRespondentImport(input: $input) {
      id
      status
      fileName
      messages
      createdOn
      updatedOn
    }
  }
`;

/**
{
 "input": {
    "accountId": "123",
    "importId": "123"
  }
}
 */
const DELETE_RESPONDENT_IMPORT = gql`
  mutation deleteRespondentImport($input: DeleteRespondentImportInput!) {
    deleteRespondentImport(input: $input)
  }
`;

// https://codesandbox.io/s/apollo-and-react-hooks-4vril
// https://github.com/apollographql/react-apollo/tree/master/packages/hooks
const RespondentImportsContainer = ({ children, variables }) => {
  const { loading, error, data } = useQuery(ALL_RESPONDENT_IMPORTS, { variables });
  const [activateImportMutation, activateImportResult] = useMutation(ACTIVATE_RESPONDENT_IMPORT, {
    refetchQueries: ['allRespondentImports'],
  });
  const [deleteImportMutation, deleteImportResult] = useMutation(DELETE_RESPONDENT_IMPORT, {
    refetchQueries: ['allRespondentImports'],
  });

  return children({
    imports: { loading, data, error },
    activateImport: { mutation: activateImportMutation, result: activateImportResult },
    deleteImport: { mutation: deleteImportMutation, result: deleteImportResult },
  });
}

const RespondentImportsContainerWrapper = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const location = useLocation();
  const params = parse(location.search);
  const pagination = filterPaginationWithSize(params);
  const variables = {
    ...pagination,
    accountId: currentAccount,
    sort: params.sort,
    filter: params.filter,
  };
  return (
    <RespondentImportsContainer variables={variables}>
      {({ imports: { data, loading, error }, activateImport, deleteImport }) => {
        if (error) {
          growl.show({ severity: 'error', detail: error.message, sticky: true });
        }

        if (data == null && data.account == null) {
          return <PageNotFound />;
        }

        const connection = data && data.account ? data.account.respondentImports : emptyConnection();

        return (
          <RespondentImportList
            connection={connection}
            activateImport={activateImport}
            deleteImport={deleteImport}
            loading={loading}
          />
        );
      }}
    </RespondentImportsContainer>
  );
}

export default RespondentImportsContainerWrapper;
